import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { GoldButton } from "../components/Buttons";
import { MaskedLines, Reveal } from "../components/Reveal";
import { FAQS, CONTACT } from "../data/content";
import Seo from "../components/Seo";

export default function FAQ() {
    return (
        <div data-testid="page-faq">
            <Seo
                title="Tax FAQ — Process, Documents & Pricing | Freedom Tax Filer"
                description="Answers about getting started, documents needed, gig-worker filing, multi-state returns, pricing, and how your information stays safe with Freedom Tax Filer."
                path="/faq"
            />
            <section className="relative pt-44 pb-14 bg-[#111318] overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(200,164,93,0.10),transparent_60%)]" />
                <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <p className="eyebrow-dark">Frequently Asked Questions</p>
                        <div className="gold-rule mt-5" />
                    </Reveal>
                    <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight mt-8 max-w-3xl">
                        <MaskedLines delay={0.2} lines={["Answers, before", <>you even <em className="italic text-gold-gradient">ask.</em></>]} />
                    </h1>
                    <Reveal delay={0.5}>
                        <p className="text-white/70 text-lg leading-relaxed mt-8 max-w-2xl">
                            Straight answers about working with us. If your question is not here, a call or a short note
                            will get it answered.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="pb-24" data-testid="faq-list">
                <div className="mx-auto max-w-4xl px-5 sm:px-8">
                    <Reveal>
                        <Accordion type="single" collapsible className="border-t border-[#E5E2DA]">
                            {FAQS.map((f, i) => (
                                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-[#E5E2DA]" data-testid={`faq-item-${i}`}>
                                    <AccordionTrigger className="font-display text-lg sm:text-xl text-[#20242B] hover:text-[#8A6D2F] hover:no-underline text-left py-6 px-0">
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="text-[#737985] leading-relaxed pb-6 px-0">{f.a}</AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <div className="mt-16 border border-[#C8A45D]/40 bg-[#111318] rounded-2xl p-10 sm:p-12" data-testid="faq-cta-card">
                            <p className="eyebrow-dark">Still Have Questions?</p>
                            <h2 className="font-display text-2xl sm:text-4xl text-white mt-5 leading-tight">Talk it through with a person.</h2>
                            <p className="text-white/60 leading-relaxed mt-4 max-w-lg">
                                Call us or send a note through the contact form — either way, a person reads it and gets
                                back to you promptly.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 mt-8">
                                <GoldButton asChild data-testid="faq-cta-contact">
                                    <Link to="/contact">
                                        Contact Us <ArrowRight className="h-4 w-4" />
                                    </Link>
                                </GoldButton>
                                {CONTACT.phoneHref && (
                                    <GoldButton asChild className="bg-transparent border border-white/25 text-white hover:border-[#C8A45D] hover:text-[#C8A45D] hover:bg-transparent" data-testid="faq-cta-call">
                                        <a href={CONTACT.phoneHref}>
                                            <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
                                        </a>
                                    </GoldButton>
                                )}
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
